/**
 * Studio's `/app-signin` page: the one place a published app or the apps portal gets a sign-in.
 *
 * They hold no Studio session. They send the visitor here with where to come back (`return`),
 * which app (`app`, its project id) or the launcher (`portal=1`), and the app's organization
 * (`org`). Signed in to Studio, the page asks the API for that app's token and goes back with it
 * in the URL fragment, which no server ever sees. The API decides whether `return` is that app's
 * own address; this page never sends a token anywhere it did not accept.
 */
import { STUDIO_API_BASE_URL, apiFetch, getAuthHeaders } from '@/api/apiClient';

export type AppSignInRequest =
  | { kind: 'app'; projectId: string; organizationId?: string; returnTo: string; switched: boolean }
  | { kind: 'portal'; returnTo: string; switched: boolean };

export type AppSignInOutcome =
  | { kind: 'go'; url: string }
  | { kind: 'switch'; organizationId: string }
  | { kind: 'denied' }
  | { kind: 'unavailable' }
  | { kind: 'failed' };

export const APP_TOKEN_FRAGMENT = 'studio_app_token';
const PROJECT_ID = /^[A-Za-z0-9_-]{1,128}$/;
const STUDIO_ORG_ID = /^org_[A-Za-z0-9]{1,64}$/;

/** An app or the portal signing its visitor out: Studio ends its own session too, or the next app would sign straight back in. */
export function isAppSignOut(search: string): boolean {
  return new URLSearchParams(search).get('signout') === '1';
}

export function readAppSignInRequest(search: string): AppSignInRequest | null {
  const params = new URLSearchParams(search);
  const returnTo = params.get('return') ?? '';
  try {
    const url = new URL(returnTo);
    if (url.protocol !== 'https:' && url.hostname !== 'localhost') return null;
  } catch {
    return null;
  }
  const switched = params.get('switched') === '1';
  if (params.get('portal') === '1') return { kind: 'portal', returnTo, switched };
  const projectId = params.get('app') ?? '';
  if (!PROJECT_ID.test(projectId)) return null;
  const org = params.get('org') ?? '';
  return { kind: 'app', projectId, ...(STUDIO_ORG_ID.test(org) ? { organizationId: org } : {}), returnTo, switched };
}

/** The address to come back to with the token, in the fragment and nowhere else. */
export function returnWithToken(returnTo: string, token: string, expiresAt: number): string {
  const url = new URL(returnTo);
  url.hash = new URLSearchParams({ [APP_TOKEN_FRAGMENT]: token, expires_at: String(expiresAt) }).toString();
  return url.toString();
}

/** This page's own address again, marked as already switched, so a second mismatch stops instead of looping. */
export function afterSwitch(pathname: string, search: string): string {
  const params = new URLSearchParams(search);
  params.set('switched', '1');
  return `${pathname}?${params.toString()}`;
}

export async function requestAppToken(request: AppSignInRequest, fetcher: typeof apiFetch = apiFetch): Promise<AppSignInOutcome> {
  const body = request.kind === 'portal'
    ? { portal: true, returnTo: request.returnTo }
    : { projectId: request.projectId, ...(request.organizationId ? { organizationId: request.organizationId } : {}), returnTo: request.returnTo };
  let response: Response;
  try {
    response = await fetcher(`${STUDIO_API_BASE_URL}/api/studio/app-tokens`, {
      method: 'POST', headers: await getAuthHeaders(), body: JSON.stringify(body),
    });
  } catch {
    return { kind: 'failed' };
  }
  const payload = (await response.json().catch(() => null)) as { token?: unknown; expiresAt?: unknown; code?: unknown; organizationId?: unknown } | null;
  if (response.ok && typeof payload?.token === 'string' && typeof payload.expiresAt === 'number') {
    return { kind: 'go', url: returnWithToken(request.returnTo, payload.token, payload.expiresAt) };
  }
  if (response.status === 409 && payload?.code === 'switch_organization' && typeof payload.organizationId === 'string' && !request.switched) {
    return { kind: 'switch', organizationId: payload.organizationId };
  }
  if (response.status === 403 || response.status === 409) return { kind: 'denied' };
  if (response.status === 503 && payload?.code === 'app_signin_unavailable') return { kind: 'unavailable' };
  return { kind: 'failed' };
}
